import { theme } from 'lib/theme';
import { View } from 'react-native';
import { ICONS } from 'components/Icon';
import { EngageInspectorCard } from './EngageInspectorCard';
import { useDispatch } from 'react-redux';
import { eventTypeInspectorsSelected } from 'store/remote/engageActions';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { doEngageOpenInspectorSelector } from 'store/actions';
import { doAddEvent, doRemoveEvents } from 'lib/events';
import { EngageInspector } from 'types/Engage';
import { randomKey } from 'lib/riskbase';
import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { chunk } from 'lodash';

export const EngageScheduledVisitInspectors: React.FC<{
  propertyId: string;
  inspectors: EngageInspector[];
  onInspectorsChanged: (inspectors: EngageInspector[]) => void;
  canEdit: boolean;
  emptyStateText?: string;
}> = ({
  propertyId,
  inspectors,
  onInspectorsChanged,
  canEdit,
  emptyStateText,
}) => {
  const dispatch = useDispatch();
  const [screenWidth, setScreenWidth] = useState(0);

  const getColumnCount = useCallback((): number => {
    if (screenWidth < 600) {
      return 1;
    } else if (screenWidth >= 600 && screenWidth < 860) {
      return 2;
    } else {
      return 3;
    }
  }, [screenWidth]);

  const columnCount = useMemo(() => getColumnCount(), [getColumnCount]);

  const eventKey = useRef(randomKey());

  const onAddPressed = () => {
    dispatch(
      doEngageOpenInspectorSelector({
        key: eventKey.current,
        propertyId: propertyId,
        inspectorIds: inspectors.map((inspector) => inspector.id),
      }),
    );
  };

  const removeInspector = useCallback(
    (id: string) => {
      onInspectorsChanged(
        inspectors.filter((inspector) => inspector.id !== id),
      );
    },
    [inspectors, onInspectorsChanged],
  );

  useEffect(() => {
    const events = [
      doAddEvent(eventTypeInspectorsSelected, (event) => {
        if (event.payload.eventKey === eventKey.current) {
          onInspectorsChanged(event.payload.accounts);
        }
      }),
    ];
    return () => {
      doRemoveEvents(events);
    };
  }, [onInspectorsChanged]);

  return (
    <View onLayout={(event) => setScreenWidth(event.nativeEvent.layout.width)}>
      <Card label="Inspection Team" labelSize="large" margin={false} />
      <View style={{ gap: theme.paddingHorizontalHalf }}>
        {!inspectors.length && (
          <Card margin={false}>
            <CardRow
              value={
                emptyStateText ||
                (canEdit
                  ? 'No inspectors assigned yet. Use "Add Inspectors" to assign an inspection team.'
                  : 'No inspectors assigned for this visit.')
              }
            />
          </Card>
        )}
        {!!inspectors.length && (
          <View
            style={{
              marginHorizontal: theme.paddingHorizontal,
              display: 'flex',
              flexDirection: 'column',
              gap: theme.paddingHorizontalHalf,
            }}
          >
            {chunk(inspectors, columnCount).map((inspectorsChunk) => {
              const emptyItems = new Array(
                columnCount - inspectorsChunk.length,
              ).fill(1);
              return (
                <View
                  key={inspectorsChunk.map(({ id }) => id).join('-')}
                  style={{
                    flex: 1,
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    gap: theme.paddingHorizontalHalf,
                  }}
                >
                  {inspectorsChunk.map((inspector) => {
                    return (
                      <EngageInspectorCard
                        key={inspector.id}
                        id={inspector.id}
                        email={inspector.email}
                        name={`${inspector.forename} ${inspector.surname}`}
                        avatarUrl={inspector.avatarUrl}
                        onRemovePressed={canEdit ? removeInspector : undefined}
                      />
                    );
                  })}
                  {emptyItems.map((_, index) => (
                    <View key={index} style={{ flex: 1 }}></View>
                  ))}
                </View>
              );
            })}
          </View>
        )}
        {canEdit && (
          <Card margin={false}>
            <CardRow
              label="Add Inspectors"
              labelColor={theme.colors.blue}
              icon={ICONS.PlusLight}
              iconColor={theme.colors.blue}
              onPress={onAddPressed}
            />
          </Card>
        )}
      </View>
    </View>
  );
};
